/*
 * -----------------------------------------------------------------------
 *  <copyright file="fragments.ts" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { gql } from '@apollo/client';

export const PartialDateFragment = gql`
    fragment PartialDateFields on PartialDateTime {
        time
        ignore
    }
`;

export const NullablePartialDateFragment = gql`
    fragment NullablePartialDateFields on NullablePartialDateTime {
        time
        ignore
    }
`;

export const UserFragment = gql`
    fragment UserFields on User {
        id
        organisation {
            id
            name
        }        
        created
        updated
        deleted
        firstName
        lastName
        email
        failedLogins
        systemAdminLevel
        phone
        isEnabled
        recordId
        lastLoginTime
    }
`;

export const SystemUserFragment = gql`
    fragment SystemUserFields on User {
        id
        organisation {
            id
            name
        }        
        created
        createdBy {
            id
            firstName
            lastName
        }
        updated
        updatedBy {
            id
            firstName
            lastName
        }
        status
        deleted
        firstName
        lastName
        email
        failedLogins
        systemAdminLevel
        phone
        isEnabled
        recordId
        lastLoginTime
    }
`;

export const UserAndRoleFragment = gql`
    fragment UserAndRoleFields on User {
        ...UserFields
        isAdmin
        organisation {
            id
            name
        }
        role {
            id
            name   
            rights {
                moduleId
                element
                permissions
            }
        }
    }
    ${UserFragment}
`;

export const OrganisationMemberFragment = gql`
    fragment OrganisationMemberFields on OrganisationMember {
        id
        created
        createdBy {
            firstName
            lastName
        }
        updated
        updatedBy {
            firstName
            lastName
        }
        deleted
        createdById
        defaultStudyId
        isAdmin
        isAuditAdmin
        isReportAdmin
        isObsAndLogsAdmin
        roleId
        recordId
        status
        email
        firstName
        lastName
        phone
        isActive
        user {
            ...UserFields
        }
        userId
    }
    ${UserFragment}
`;

export const OrganisationMemberAuditFragment = gql`
    fragment OrganisationMemberAuditFields on OrganisationMember {
        id        
        auditId
        updated
        defaultStudyId
        isAdmin
        isAuditAdmin
        isReportAdmin
        roleId
        role {
            id
            name
        }
        recordId
        status
        email
        firstName
        lastName
        phone
        isActive
        userId
        defaultStudy {
            shortName
            studyNumber
        }
    }
`;

export const OrganisationMemberAndUserFragment = gql`
    fragment OrganisationMemberAndUserFields on OrganisationMember {
        ...OrganisationMemberFields        
    }
    ${OrganisationMemberFragment}
`;

export const OrganisationPrimaryContactFragment = gql`
    fragment OrganisationPrimaryContactFields on OrganisationContact {
        firstName
        lastName
        email
        phone
        mobile
    }
`;

export const OrganisationProductFragment = gql`
    fragment OrganisationProductFields on OrganisationProduct {
        auditId
        id
        recordId
        created
        createdBy {
            firstName
            lastName
        }
        updated
        updatedBy {
            firstName
            lastName
        }
        deleted
        name
        activeIngredients
        code
        registrationNumber
        referenceNumber
    }
`;

export const OrganisationIndicationFragment = gql`
    fragment OrganisationIndicationFields on OrganisationIndication {
        auditId
        id
        deleted
        name
        created
        createdBy {
            id
            firstName
            lastName
        }
        updated
        recordId
        updatedBy {
            id
            firstName
            lastName
        }
    }
`;

export const OrganisationProgramFragment = gql`
    fragment OrganisationProgramFields on OrganisationProgram {
        id
        deleted
        name
        productIds
        indicationIds
        recordId
        created
        createdBy {
            id
            firstName
            lastName
        }
        updated
        updatedBy {
            id
            firstName
            lastName
        }
    }
`;

export const OrganisationProgramAuditFragment = gql`
    fragment OrganisationProgramAuditFields on OrganisationProgram {
        ...OrganisationProgramFields
        auditId
        indications {
            id
            name
        }
        products {
            id
            name
        }
    }
    ${OrganisationProgramFragment}
`;

export const OrganisationRoleFragment = gql`
    fragment OrganisationRoleFields on OrganisationRole {
        auditId
        id
        organisationId        
        created
        updated
        deleted
        active
        name
        description
        modules
        rights {
            moduleId
            element
            permissions
        }
    }
`;

export const RiskEvaluationModelItemFragment = gql`
    fragment RiskEvaluationModelItemFields on RiskEvaluationModelItem {
        value
        enabled
        label
        description
    }
`;
export const RiskEvaluationModelFragment = gql`
    fragment RiskEvaluationModelFields on RiskEvaluationModel {
        auditId
        id
        created
        updated
        updatedBy {
            id
            firstName
            lastName
        }
        createdBy {
            id
            firstName
            lastName
        }
        deleted
        name
        active
        recordId
        includeDetectability
        likelihood {
            ...RiskEvaluationModelItemFields
        }
        impact {
            ...RiskEvaluationModelItemFields
        }
        detectability {
            ...RiskEvaluationModelItemFields
        }
        reviewPeriodDays
        ragMap {
            x
            y
            colour
        }
    }
    ${RiskEvaluationModelItemFragment}
`;

export const SystemOrganisationFragment = gql`
    fragment SystemOrganisationFields on SystemOrganisation {
        auditId
        id
        key
        name
        loginCode
        apiKey
        created
        createdBy {
            firstName
            lastName
        }
        updated
        updatedBy {
            firstName
            lastName
        }
        config {
            mongoRegion            
            custom {
                connectionString
                useTransactions
            }
        }
        parentSnapshot
    }
`;

export const SystemOrganisationKeyNameFragment = gql`
    fragment SystemOrganisationKeyNameFields on SystemOrganisation {
        key
        name
    }
`;

export const FieldDefinitionFragment = gql`
    fragment FieldDefinitionFields on FieldDefinition {
        name
        description
        key
        resolver
        dataType
        fieldType
        tags
        isCalculated
        isHidden
        rowDataSource
    }
`;

export const OpraPluginErrorFragment = gql`
    fragment OpraPluginErrorFields on OpraPluginError {
        message
        stackTrace
        dataType
        fieldKey
        siteId
        subjectId
        itemId        
    }
`;

export const OpraPluginFragment = gql`
    fragment OpraPluginFields on OpraPlugin {
        auditId
        id
        studyId
        name  
        fileName
        version
        fields {
            ...FieldDefinitionFields
        }
        errors {
            ...OpraPluginErrorFields
        }        
        rowDataSources
        filters {
            id
            tags
            name
            table
        }
        transformRules
    }
    ${FieldDefinitionFragment}
    ${OpraPluginErrorFragment}
`;

export const OrganisationSecurityFragment = gql`
    fragment OrganisationSecurityFields on OrganisationSecurity {        
        minLowercaseCount
        minUppercaseCount
        minPasswordLength
        minSpecialCharCount
        minNumberCount
        lockoutPeriodMinutes
        numAttemptsBeforeLockout
        accountInactiveDays
        twoFactorEnabled
        userIdleTimeoutMinutes
        ssoIssuer
        authenticationMethod
    }
`;

export const OrganisationFragment = gql`
    fragment OrganisationFields on Organisation {
        auditId
        logo
        id
        code
        recordId
        organisationKey
        created
        createdBy {
            firstName
            lastName
        }
        updated
        updatedBy {
            firstName
            lastName
        }
        deleted
        name
        loginCode
        apiKey
        type
        modules
        security {
            ...OrganisationSecurityFields
        }
        primaryContact {
            ...OrganisationPrimaryContactFields
        }
        products {
            ...OrganisationProductFields
        }
        indications {
            ...OrganisationIndicationFields
        }
        programs {
            ...OrganisationProgramFields
        }
        roles {
            ...OrganisationRoleFields
        }
        members {
            ...OrganisationMemberFields
        }
        evaluationModels {
            ...RiskEvaluationModelFields
        }
        studies {
            id
            studyNumber
        }
        plugin {
            ...OpraPluginFields
        }
    }
    ${OrganisationSecurityFragment}
    ${OrganisationPrimaryContactFragment}
    ${OrganisationProductFragment}
    ${OrganisationIndicationFragment}
    ${OrganisationProgramFragment}
    ${OrganisationRoleFragment}
    ${OrganisationMemberFragment}
    ${RiskEvaluationModelFragment}
    ${OpraPluginFragment}
`;

export const RestrictedOrganisationFragment = gql`
    fragment RestrictedOrganisationFields on Organisation {
        id
        code
        recordId
        organisationKey
        name
        loginCode
        apiKey
        type
        modules
        members {
            ...OrganisationMemberFields
        }
        evaluationModels {
            ...RiskEvaluationModelFields
        }
        studies {
            id
            studyNumber
        }
    }
    ${OrganisationMemberFragment}
    ${RiskEvaluationModelFragment}
`;

export const OrganisationFragmentForProductProgram = gql`
    fragment OrganisationFieldsForProductProgram on Organisation {
        id
        recordId
        organisationKey
        created
        updated
        deleted
        name
        loginCode
        apiKey
        type
        modules
        products {
            ...OrganisationProductFields
        }
        indications {
            ...OrganisationIndicationFields
        }
        programs {
            ...OrganisationProgramFields
        }
        studies {
            id
            studyNumber
            programsIds
            productsIds
            indicationsIds
        }
    }
    ${OrganisationProductFragment}
    ${OrganisationIndicationFragment}
    ${OrganisationProgramFragment}
`;

export const OrganisationAndUserFragment = gql`
    fragment OrganisationAndUserFields on Organisation {
        id
        recordId
        created
        organisationId
        organisationKey
        updated
        deleted
        name
        loginCode
        apiKey
        type
        logo
        primaryContact {
            ...OrganisationPrimaryContactFields
        }
        products {
            ...OrganisationProductFields
        }
        indications {
            ...OrganisationIndicationFields
        }
        programs {
            ...OrganisationProgramFields
        }
        roles {
            ...OrganisationRoleFields
        }
        members {
            ...OrganisationMemberAndUserFields
        }
        evaluationModels {
            ...RiskEvaluationModelFields
        }
        studies {
            id
            studyNumber
        }
        plugin {
            ...OpraPluginFields
        }
    }
    ${OrganisationPrimaryContactFragment}
    ${OrganisationProductFragment}
    ${OrganisationIndicationFragment}
    ${OrganisationProgramFragment}
    ${OrganisationRoleFragment}
    ${OrganisationMemberAndUserFragment}
    ${RiskEvaluationModelFragment}
    ${OpraPluginFragment}
`;

export const StudyMemberFragment = gql`
    fragment StudyMemberFields on StudyMember {
        id
        recordId
        created
        createdBy {
            id
            firstName
            lastName
        }
        updated
        updatedBy {
            id
            firstName
            lastName
        }
        deleted
        organisationRole {
            id   
            name
        }
        favourite
        favouriteSiteIds
        email
        status
        active
        isAdmin
        defaultSiteId
        firstName
        lastName
        userId
        user {
            ...UserFields
        }
    }
    ${UserFragment}
`;

export const SmStudyMemberFragment = gql`
    fragment SmStudyMemberFields on SmStudyMember {
        id
        recordId
        created
        createdBy {
            id
            firstName
            lastName
        }
        updated
        updatedBy {
            id
            firstName
            lastName
        }
        deleted
        organisationRole {
            id   
            name
        }
        favourite
        favouriteSiteIds
        email
        status
        active
        isAdmin
        defaultSiteId
        firstName
        lastName
        userId
        user {
            ...UserFields
        }
    }
    ${UserFragment}
`;

export const StudyMemberAuditFragment = gql`
    fragment StudyMemberAuditFields on StudyMember {
        id
        auditId
        updated
        recordId
        favourite
        favouriteSiteIds
        favouriteSites {
            id
            name
        }
        email
        status
        active
        isAdmin
        defaultSiteId
        defaultSite {
            name
        }
        firstName
        lastName
        userId
    }
`;

export const RuleFragment = gql`
    fragment RuleFields on Rule {
        id
        index
        ruleClause
        comparatorField
        ruleClauseOperator
        value
    }
`;

export const StudyCrfFragment = gql`
    fragment StudyCrfFields on StudyCrf {
        auditId
        id
        crfName
        active
        required
        recordId
        subjectCode
        updated
        updatedBy {
            firstName
            lastName
        }
        created
        createdBy {
            firstName
            lastName
        }
        crfRules {
            ...RuleFields
        }
        configId
    }
    ${RuleFragment}
`;

export const StudyTreatmentArmFragment = gql`
    fragment StudyTreatmentArmFields on StudyTreatmentArm {
        auditId
        id
        name
        subjectCode
        description
        recordId
        updated
        active
        updatedBy{
            firstName
            lastName
        }
        created
        createdBy{
            firstName
            lastName
        }
        configId
    }
`;

export const StudyCohortFragment = gql`
    fragment StudyCohortFields on StudyCohort {
        auditId
        id
        configId
        name
        subjectCode
        description
        recordId
        created
        active
        createdBy {
            id
            firstName
            lastName
        }
        updated
        updatedBy {
            id
            firstName
            lastName
        }
    }
`;

export const StudyAssessmentFragment = gql`
    fragment StudyAssessmentFields on StudyAssessment {
        id
        active
        auditId
        created
        crfId
        createdBy {
            id
            firstName
            lastName
        }
        updated
        updatedBy {
            id
            firstName
            lastName
        }
        deleted
        recordId
        name
        endPoint
        safety
        sendToVendor
        lab
        completedFieldName
        assessmentRules {
            ...RuleFields
        }
        configId
    }
    ${RuleFragment}
`;

export const StudySubjectVisitFragment = gql`
    fragment StudySubjectVisitFields on StudySubjectVisit {
        auditId
        id
        recordId
        created
        createdBy {
            id
            firstName
            lastName
        }
        updated
        updatedBy {
            id
            firstName
            lastName
        }
        crfs {
            ...StudyCrfFields
        }
        assessments {
            ...StudyAssessmentFields
        }
        eventIds
        forecastEndDateField
        isEndDateEarliest
        leadPeriod
        leadUnit
        leadVisitId
        minOccurrences
        maxOccurrences
        minWinPeriod
        minWinUnit
        maxWinPeriod
        maxWinUnit
        type
        visitId
        visitName
        sequenceNumber
        active
        configId
    }
    ${StudyCrfFragment}
    ${StudyAssessmentFragment}
`;

export const StudyVisitScheduleFragment = gql`
    fragment StudyVisitScheduleFields on StudySubjectVisitScheduleTemplate {
        id
        recordId
        created
        updated
        cohortIds
        description
        treatmentArmIds
        name
        code
        active
        recordId
        createdBy {
            id
            firstName
            lastName
        }
        updatedBy {
            id
            firstName
            lastName
        }
        visits {
            ...StudySubjectVisitFields
        }
        version
        versionDate {
            ...NullablePartialDateFields
        }
        groupId
        configId
    }
    ${StudySubjectVisitFragment}    
    ${NullablePartialDateFragment}
`;
export const StudyVisitScheduleRuleFieldFragment = gql`
    fragment StudyVisitScheduleRuleFieldFields on StudySVTRuleFieldType {
        fieldName
        fieldType
        friendlyName
    }
`;


export const StudySvtAuditFragment = gql`
    fragment StudySvtAuditFields on StudySubjectVisitScheduleTemplate {
        id
        auditId
        updated
        cohortIds
        cohorts {
            id
            name
        }
        treatmentArmIds
        treatmentArms {
            id
            name
        }
        name
        code
        active
        visitIds
        visits {
            id
            eventIds
            leadPeriod
            leadUnit
            leadVisitId
            minOccurrences
            maxOccurrences
            minWinPeriod
            minWinUnit
            maxWinPeriod
            maxWinUnit
            type
            visitId
            visitName
            sequenceNumber
            active
        }
        version
        versionDate {
            ...NullablePartialDateFields
        }
        groupId
    }
    ${NullablePartialDateFragment}
`;

export const StudyAndMemberFragment = gql`
    fragment StudyAndMemberFields on Study {
        id
        created
        updated
        deleted
        studyNumber
        shortName
        title
        riskReviewWindowDays
        code
        modules
        programsIds
        productsIds
        indicationsIds
        studyReadOnly
        members {
            ...StudyMemberFields
        }      
        smMembers {
            ...SmStudyMemberFields       
        }
    }
    ${StudyMemberFragment}
    ${SmStudyMemberFragment}
`;

export const StudyFragment = gql`
    fragment StudyFields on Study {
        id
        configId
        created
        createdBy {
            id
            firstName
            lastName
        }
        updated
        updatedBy {
            id
            firstName
            lastName
        }
        deleted
        recordId
        studyNumber
        shortName
        title
        logo
        riskReviewWindowDays
        code
        modules
        programsIds
        productsIds
        indicationsIds
        numSites
        importDefinitionsKey
        sponsor
        studyFunctionIds
        riskAssessmentAwardStartDate
        riskAssessmentIsmpStartDate
        riskAssessmentRfpStartDate
        centralMonitoringFirstDataLoadDate
        centralMonitoringReleaseDate
        scoreGreenThreshold
        scoreAmberThreshold
        volumeAdjustedScoring
        scoreThreshold
        adjustmentWeight
        volumeAdjustedScoringNumberField
        sitesToExcludeIds
        sitesStatusesToExclude
        calculateAssessments
        calculateCrfs
        calculateVisits
        svtDependency
        lastSubjectEnrolledDate
        lastSubjectRandomisedDate
        lastSubjectScreenedDate
        importDefinitionsKey
        riskOverviewConfiguration {
            subjectField
            amberKris
            redKris
            amberDecisionKris
            redDecisionKris
            siteStatus
            siteActiveDate
            siteCountry
            siteMonitor
            numSubjects
        }
        studySummaryConfiguration {
            numScreened
            numScreenFailed
            numEnrolled
            numRandomised
            numTreatmentDiscontinued
            numStudyDiscontinued
            numCompleted
        }
        siteSummaryConfiguration {
            siteActiveDate
            siteCountry
            siteStatus
            monitor
            numScreened
            numScreenFailed
            numEnrolled
            numRandomised
            numTreatmentDiscontinued
            numStudyDiscontinued
            numCompleted
            observationChart
            actionChart
        }
        riskLibraryCategories {
            id
            name
        }
        crfs {
            id
            crfName
        }
        riskLibraryCategoryIds
        organisation {
            ...OrganisationFields
        }
        members {
            ...StudyMemberFields
        }
        evaluationModelId
        evaluationModel {
            ...RiskEvaluationModelFields
        }
        useInformedConsentedDate
        assessmentAssociation
        displayRiskOverviewOnStudySummary
        displayStudyOverviewOnStudySummary
        displayStudySiteOverviewOnStudySummary
        studyOverviewTableConfiguration {
            columnConfigurations {
                order
                name
                type
                dataSource
                fields
                calculation
                default
            }
            panelConfigurations {
                panelId
                panelOrder
            }
        }
        studySiteOverviewTableConfiguration {
            columnConfigurations {
                order
                name
                type
                dataSource
                fields
                calculation
                default
            }
            panelConfigurations {
                panelId
                panelOrder
            }
            rowOrder
        }
        useLatestCentralMonitoring
        studyReadOnly
        automationActive
        studyAutomationColor
        controlsAutomationActive
        controlsAutomationColor
        integrationActive
        integrationStudyNumber
        hasIntegrationActions
        ctqScreenWidgetsConfiguration {
            numberTotal
            numberActive
            numberActiveCritical
            numberActiveCtQsWithoutRisks
            numberActiveCtQsWithoutFunctionalDataReviews
        }
        smMembers {
            ...SmStudyMemberFields
        }
    }
    ${SmStudyMemberFragment}
    ${OrganisationFragment}
    ${StudyMemberFragment}
    ${RiskEvaluationModelFragment}
`;

export const NavbarStudyFragment = gql`
    fragment NavbarStudyFields on Study {
        id
        studyNumber
        shortName
        modules
        members {
            userId
            favourite
            active
        }
        smMembers {
            userId
            favourite
            active
        }
    }
`;

export const StudyPluginFragment = gql`
    fragment StudyPluginFields on Study {
        id
        plugin {
            ...OpraPluginFields
        }
    }
    ${OpraPluginFragment}
`;

export const StudyRestrictedFragment = gql`
    fragment StudyRestrictedFields on Study {
        id
        recordId
        studyNumber
        shortName
        title
        modules
        numSites
        sponsor
        studyFunctionIds
        sitesToExcludeIds
        sitesStatusesToExclude
        riskLibraryCategoryIds
        displayRiskOverviewOnStudySummary
        displayStudyOverviewOnStudySummary
        displayStudySiteOverviewOnStudySummary
        organisation {
            ...RestrictedOrganisationFields
        }
        members {
            ...StudyMemberFields
        }
        evaluationModelId
        evaluationModel {
            ...RiskEvaluationModelFields
        }
        smMembers {
            ...SmStudyMemberFields      
        }
    }
    ${RestrictedOrganisationFragment}
    ${StudyMemberFragment}
    ${RiskEvaluationModelFragment}
    ${SmStudyMemberFragment}
`;

export const StudyAuditFragment = gql`
    fragment StudyAuditFields on Study {
        id
        auditId
        updated
        recordId
        studyNumber
        shortName
        title
        riskReviewWindowDays
        code
        modules
        programsIds
        programs {
            id
            name
        }
        productsIds
        products {
            id
            name
        }
        indicationsIds 
        indications {
            id
            name
        }
        studyFunctionIds
        studyFunction {
            id
            name
        }
        numSites
        sponsor
        riskAssessmentAwardStartDate
        riskAssessmentIsmpStartDate
        riskAssessmentRfpStartDate
        centralMonitoringFirstDataLoadDate
        centralMonitoringReleaseDate
        scoreGreenThreshold
        scoreAmberThreshold
        volumeAdjustedScoring
        scoreThreshold
        adjustmentWeight
        volumeAdjustedScoringNumberField
        sitesToExcludeIds
        sitesStatusesToExclude
        sitesToExclude {
            id
            name
        }
        riskLibraryCategories {
            id
            name
        }
        evaluationModelId
        evaluationModel {
            name
        }
        sitesStatusesToExcludeFromAutomation
    }    
`;

export const ManageStudyFragment = gql`
    fragment ManageStudyFields on Study {
        id
        created
        createdBy {
            id
            firstName
            lastName 
        }
        updated
        updatedBy {
            id
            firstName
            lastName
        }
        deleted
        studyNumber
        shortName        
        title
        logo
        riskReviewWindowDays
        riskLibraryCategories {
            id
            name
        }
        modules
        programsIds
        productsIds
        indicationsIds
        evaluationModelId
        sponsor
        riskAssessmentAwardStartDate
        riskAssessmentIsmpStartDate
        riskAssessmentRfpStartDate
        centralMonitoringFirstDataLoadDate
        centralMonitoringReleaseDate
        recordId
        scoreThreshold,
        volumeAdjustedScoring,
        adjustmentWeight,
        volumeAdjustedScoringNumberField,
        calculateAssessments,
        calculateCrfs,
        calculateVisits,
        svtDependency,
        scoreGreenThreshold,
        scoreAmberThreshold,
        importDefinitionsKey,
        sitesStatusesToExclude
        configId
        useLatestCentralMonitoring
        studyReadOnly
    }
`;

export const OrganisationAndStudyMemberFragment = gql`
    fragment OrganisationAndStudyMemberFields on Organisation {
        id
        recordId
        created
        organisationId
        organisationKey
        updated
        deleted
        name
        loginCode
        apiKey
        type
        logo
        security {
            ...OrganisationSecurityFields
        }
        roles {
            ...OrganisationRoleFields
        }
        members {
            ...OrganisationMemberAndUserFields
        }
        studies {
            ...StudyAndMemberFields
        }
    }
    ${OrganisationSecurityFragment}
    ${OrganisationRoleFragment}
    ${OrganisationMemberAndUserFragment}
    ${StudyAndMemberFragment}
    ${SmStudyMemberFragment}
`;

export const SiteFragment = gql`
    fragment SiteFields on Site {
        id
        created
        updated
        deleted
        name
        code
        disabled
        activeDate
        status
        monitor      
    }
`;

export const SiteIssueFragment = gql`
    fragment SiteIssueFields on SiteIssue {
        id
        created
        updated
        code
        type
        description
        dateOccurred
        status
        action
        dateResolved
        owner
        monitor
    }
`;

export const SiteProtocolDeviationFragment = gql`
    fragment SiteProtocolDeviationFields on SiteProtocolDeviation {
        id
        created
        updated
        code
        occurDate {
            ...NullablePartialDateFields
        }
        escalated
        classification
        description
        category
    }
    ${NullablePartialDateFragment}
`;

export const SiteVisitFragment = gql`
    fragment SiteVisitFields on SiteVisit {
        id
        created
        updated
        code
        type
        date {
            ...PartialDateFields
        }
        numDaysOnSite
        numHoursOnSite
    }
    ${PartialDateFragment}
`;

export const SubjectFragment = gql`
    fragment SubjectFields on Subject {
        id
        siteId
        created
        updated
        status
        code
        subjectNumber
        gender
        ageAtEnrollment
        yearOfBirth
        ethnicity
        race
        screeningDate {
            ...NullablePartialDateFields
        }
        screeningFailDate {
            ...NullablePartialDateFields
        }
        screeningFailReason
        treatmentDiscontinuedDate {
            ...NullablePartialDateFields
        }
        treatmentDiscontinuedReason
        studyDiscontinuedDate {
            ...NullablePartialDateFields
        }
        studyDiscontinuedReason
        enrolledDate {
            ...NullablePartialDateFields
        }
        numAssessments
        numCrfs
        numInformedConsents
        numAdverseEvents
        numIssues
        numProtocolDeviations
        numVisits
        numLabs
        numQueries
    }
    ${NullablePartialDateFragment}
`;

export const SubjectAssessmentFragment = gql`
    fragment SubjectAssessmentFields on SubjectAssessment {
        id
        created
        updated
        visitId
        code
        name
        endPoint
        bpSystolic
        bpDiastolic
        completedDate {
            ...NullablePartialDateFields
        }
        sendToVendor
        vendorReceivedDate {
            ...NullablePartialDateFields
        }
        expected
        completed
        missed
        notDone
        unscheduled
        safety
    }
    ${NullablePartialDateFragment}
`;

export const SubjectIssueFragment = gql`
    fragment SubjectIssueFields on SubjectIssue {
        id
        created
        updated
        code
        type
        description
        dateOccurred {
            ...PartialDateFields
        }
        action
        dateResolved {
            ...NullablePartialDateFields
        }
        status
        owner
        monitor
    }
    ${PartialDateFragment}
    ${NullablePartialDateFragment}
`;

export const SubjectProtocolDeviationFragment = gql`
    fragment SubjectProtocolDeviationFields on SubjectProtocolDeviation {
        id
        created
        updated
        code
        occurDate {
            ...NullablePartialDateFields
        }
        escalated
        classification
        reason
        category
    }
    ${NullablePartialDateFragment}
`;

export const SubjectCrfFragment = gql`
    fragment SubjectCrfFields on SubjectCRF {
        id
        created
        updated
        visitId
        code
        name
        firstDataEntryDate
        lastDataEntryDate
        expected
        completed
        missed
        notDone
    }
    ${PartialDateFragment}
`;

export const SubjectAdverseEventFragment = gql`
    fragment SubjectAdverseEventFields on SubjectAdverseEvent {
        id
        created
        updated
        code
        relateStudyDrug
        term
        isSAE
        saeAwareDate {
            ...NullablePartialDateFields
        }
        saeInformDate {
            ...NullablePartialDateFields
        }
        startDate {
            ...NullablePartialDateFields
        }
        hltCodedTerm
        lltCodedTerm
        hlGroupTerm
        severity
        actionTakenStudyDrug
        endDate {
            ...NullablePartialDateFields
        }
        outcome
    }
    ${NullablePartialDateFragment}
    ${PartialDateFragment}
`;

export const SubjectInformedConsentFragment = gql`
    fragment SubjectInformedConsentFields on SubjectInformedConsent {
        id
        created
        updated
        code
        consentDate {
            ...PartialDateFields
        }
        version
    }
    ${PartialDateFragment}
`;

export const SubjectVisitFragment = gql`
    fragment SubjectVisitFields on SubjectVisit {
        id
        created
        updated
        code
        scheduleId
        sequenceNumber
        type
        visitName
        visitIndex
        expectedDate {
            ...NullablePartialDateFields
        }
        minExpectedDate {
            ...NullablePartialDateFields
        }
        maxExpectedDate {
            ...NullablePartialDateFields
        }
        completedDate {
            ...NullablePartialDateFields
        }
        status
        isUnscheduled
        isOutOfWindow
    }
    ${NullablePartialDateFragment}
`;

export const SubjectLabFragment = gql`
    fragment SubjectLabFields on SubjectLab {
        id
        created
        updated
        code
        sampleDate
        result
        lowerLimit
        upperLimit
    }
`;

export const SubjectQueryFragment = gql`
    fragment SubjectQueryFields on SubjectQuery {
        id
        created
        updated
        visitId
        crfId
        code
        type
        revisionCycle
        openedDate {
            ...PartialDateFields
        }
        answerDate {
            ...NullablePartialDateFields
        }
        closedDate {
            ...NullablePartialDateFields
        }
    }
    ${PartialDateFragment}
    ${NullablePartialDateFragment}
`;

export const StudyControlFragment = gql`
    fragment StudyControlFields on StudyControl {
        id
        recordId
        deleted
        created
        createdBy {
            id
            firstName
            lastName
        }
        updated
        updatedBy {
            id
            firstName
            lastName
        }
        studyId
        libraryControlId
        responsibleFunctionId
        owner {
            firstName
            lastName
            active
            user {
                firstName
                lastName
            }
        }
        ownerId
        typeId
        type {
            id
            name
            active
        }
        volume
        description 
        actionToBeTaken
        activeDate
        inactiveDate
        additionalInformation
        dateIdentified
        phase
        controlPhaseId
        controlPhase {
            id
            name
            active
        }
        purpose
        responsibleFunction {
            id
            name
            active
        }
        reviewFrequencyPeriod
        reviewFrequencyUnits
        threshold
        updatedBy {
            id
            firstName
            lastName
        }
        status
    }
`;

export const StudyControlAuditFragment = gql`
    fragment StudyControlAuditFields on StudyControl {
        id
        auditId
        updated
        recordId
        deleted
        created        
        studyId
        libraryControlId
        responsibleFunctionId
        owner {
            firstName
            lastName
            active
        }
        ownerId
        typeId
        type {
            id
            name
            active
        }
        volume
        description
        actionToBeTaken
        activeDate
        inactiveDate
        additionalInformation
        dateIdentified
        phase
        controlPhaseId
        controlPhase {
            id
            name
            active
        }
        purpose
        responsibleFunction {
            name
        }
        reviewFrequencyPeriod
        reviewFrequencyUnits
        threshold        
    }
`;

export const RiskEvaluationFragment = gql`
    fragment RiskEvaluationFields on RiskEvaluation {
        id
        created
        createdBy {
            id
            firstName
            lastName
        }
        updated
        updatedBy {
            id
            firstName
            lastName
        }
        deleted
        score
        movement
        likelihood
        impact
        detectability
        colour
        changeReason
        evaluationModelId
        evaluationModel {
            name
        }
        rationale
    }
`;

export const RiskEvaluationAuditFragment = gql`
    fragment RiskEvaluationAuditFields on RiskEvaluation {
        ...RiskEvaluationFields
        auditId
        evaluationModel {
            id
            name
        }
        type
    }
    ${RiskEvaluationFragment}
`;

export const RiskActionFragment = gql`
    fragment RiskActionFields on RiskAction {
        id
        created
        audit {
            reason
            created
            userFirstName
            userLastName
        }
        createdBy {
            id
            firstName
            lastName
        }
        updated
        updatedBy {
            id
            firstName
            lastName
        }
        deleted
        ownerId
        recordId
        description
        result
        comments
        status
        completedDate
        responsibleFunctionId
        responsibleFunction {
            id
            name
            active
        }
        requestDate
        expectedCompleteDate
        owner {
            firstName
            lastName
            active
            email
        }
        type
    }
`;

export const RiskActionAuditFragment = gql`
    fragment RiskActionAuditFields on RiskAction {
        id
        auditId
        updated
        ownerId
        recordId
        description
        result
        comments
        completedDate
        responsibleFunctionId
        requestDate
        expectedCompleteDate
        owner {
            firstName
            lastName
            active
            email
        }
        type
        responsibleFunction { 
            name
        }
    }
`;

export const RiskObservationFragment = gql`
    fragment RiskObservationFields on RiskObservation {
        id
        riskId
        studyId
        audit {
            reason
            created
            userFirstName
            userLastName
        }
        created
        createdBy {
            id
            firstName
            lastName
        }
        classificationId
        classification {
            id
            name
            active
        }
        updated
        updatedBy {
            id
            firstName
            lastName
        }
        observationDate
        deleted        
        recordId
        description
        potentialCause
        confirmedCause
        responsibleFunctionId
        responsibleFunction {
            id
            name
            active
        }
        owner {
            id
            firstName
            lastName
            active
        }
        ownerId
        comments
        closedDate
        qtlExceeded
        observationDate
        status
        riskStatement
        actions {
            ...RiskActionFields
        }
        resolution
        vendors {
            id
            name
            active
        }
        countries {
            code,
            name
        }
    }
    ${RiskActionFragment}
`;

export const RiskObservationAuditFragment = gql`
    fragment RiskObservationAuditFields on RiskObservation {
        id
        auditId
        updated
        riskId
        studyId
        classificationId
        observationDate
        deleted
        recordId
        description
        potentialCause
        confirmedCause
        responsibleFunctionId
        owner {
            id
            firstName
            lastName
            active
        }
        ownerId
        comments
        closedDate
        qtlExceeded
        observationDate
        actionIds 
        actions {
            id
            description
        }
        responsibleFunction {
            id
            name
            active
        }
        owner {
            firstName
            lastName
            active
        }
        classification { 
            id
            name
            active
        }
    }
`;

export const CommentReplyFragment = gql`
    fragment CommentReplyFields on CommentReply {
        id
        created
        createdBy {
            id
            firstName
            lastName
        }
        content
        version
    }
`;

export const CommentFragment = gql`
    fragment CommentFields on Comment {
        id
        created
        createdBy {
            id
            firstName
            lastName
        }
        updatedBy {
            id
        }
        content
        open
        version
        replies {
            ...CommentReplyFields
        },
    }
    ${CommentReplyFragment}
`;

export const CommentsWithDeletedItemsFragment = gql`
    fragment CommentsWithDeletedItemsFields on CommentsWithDeletedItems {
        comments {
            ...CommentFields
        }
        deletedCommentIds
        deletedReplyIds
    }
    ${CommentFragment}
`;

export const CommentWithDeletedRepliesFragment = gql`
    fragment CommentWithDeletedRepliesFields on CommentWithDeletedReplies {
        comment {
            ...CommentFields
        }
        deletedReplies
    }
    ${CommentFragment}
`;

export const CommentAuditFragment = gql`
    fragment CommentAuditFields on Comment {
        id
        created
        createdBy {
            id
            firstName
            lastName
        }
        updated
        deleted
        recordId
        content
        open
        auditId
    }
`;

export const CommentReplyAuditFragment = gql`
    fragment CommentReplyAuditFields on CommentReply {
        id
        created
        createdBy {
            id
            firstName
            lastName
        }
        updated
        deleted
        recordId
        content
        auditId
    }
`;

export const CompleteRiskFragment = gql`
    fragment CompleteRiskFields on Risk {
        id
        created
        deleted
        studyId
        createdById
        libraryReferenceId
        ownerId
        reviewStatus
        owner {
            id
            firstName
            lastName
            active
            user {
                id
                firstName
                lastName
            }
        }
        recordId
        internalOnly
        controlStrategy {
            id
            name
            active
        }
        category {
            id
            name
            active
        }        
        statement
        associatedVariableIds
        dateLastReviewed
        dateIdentified
        closedDate
        status
        reviewRequired
        reviewFrequencyPeriod
        reviewFrequencyUnits
        nextReviewDate
        controlIds
        evaluationHistory {
            ...RiskEvaluationFields
        }
        active
        updated
        createdBy {
            id
            firstName
            lastName
        }
        updatedBy {
            id
            firstName
            lastName
        }
        responsibleFunctionId
        responsibleFunction {
            id
            name
            active
        }
        vendors {
            id
            name
            active
        }
        score {
            score
            colour
            movement
            sortMovement
            prevScore
            prevColour
        }
        activeControlCount
        associatedVariables {
            id
            active
        }
        controls {
            ...StudyControlFields
        }
        observations {
            id
            deleted
            closedDate
            vendors {
                id
            }
            actions {
                id
                completedDate
                deleted
            }
        }
        countries {
            code
            name
        }
        qualityIssue {
            id
            name
            active
        }
        qualityIssueReference
        associatedControlsHubPanelIds
        commentCounts {
            open,
            all
        }
        
    }
    ${RiskEvaluationFragment}
    ${StudyControlFragment}
`;

export const RisksAssignedControlIdsFragment = gql`
    fragment RisksAssignedControlIdsFields on Risk {
        controlIds
    }
`;

export const RisksAndObservationsVendorIdsFragment = gql`
fragment RisksAndObservationsVendorIdsFields on Risk {
    observations {
        vendorIds
    }
    vendorIds
}`;

export const RisksQualityIssueIdsFragment = gql`
fragment RisksQualityIssueIdsFields on Risk {
    qualityIssue {
        id
    }
}`;

export const LibraryControlFragment = gql`
    fragment LibraryControlFields on LibraryControl {
        id
        recordId
        typeId
        type {
            id
            name
            active
        }
        description
        threshold
        action
        active
        reviewFrequencyPeriod
        reviewFrequencyUnits
        systemReferenceId
        phase
        controlPhaseId
        controlPhase {
            id
            name
            active
        }
        controlType {
            id
            name
            active
        }
        responsibleFunction {
            id
            name
            active
        }
        created
        updated
        createdBy {
            id
            firstName
            lastName
        }
        updatedBy {
            id
            firstName
            lastName
        }
    }
`;

export const LibraryControlAuditFragment = gql`
    fragment LibraryControlAuditFields on LibraryControl {
        id
        auditId
        updated
        recordId
        description
        threshold
        action
        active
        reviewFrequencyPeriod
        reviewFrequencyUnits
        systemReferenceId
        phase
        controlPhaseId
        controlPhase {
            id
            name
            active
        }
        responsibleFunction {
            id
            name
        }
        controlType {
            id
            name
        }
        typeId
        type {
            id
            name
            active
        }
    }
`;

export const LibraryRiskFragment = gql`
    fragment LibraryRiskFields on LibraryRisk {
        id
        created
        createdBy {
            firstName
            lastName
        }
        updated
        updatedBy {
            firstName
            lastName
        }
        deleted
        displayOrder
        organisationId
        studyId
        statement
        systemReferenceId
        libraryControlIds
        recordId
        riskLibraryCategoryIds
        active
        riskCategoryId
        controlStrategyId
        controlStrategy {
            id
            name
            active
        }
        responsibleFunctionId
        responsibleFunction {
            id
            name
            active
            organisationId
            studyId
        }
        reviewFrequencyPeriod
        reviewFrequencyUnits
        riskCategory {
            id
            name
            active
        }
        reviewRequired
        vendorIds
        vendors {
            id
            name
            active
        }
        countries {
            code,
            name
        }
        libraryControlIds
        libraryControls {
            ...LibraryControlFields
        }
    }
    ${LibraryControlFragment}
`;

export const LibraryRiskAuditFragment = gql`
    fragment LibraryRiskAuditFields on LibraryRisk {
        ...LibraryRiskFields
        auditId
        libraryControls {
            id
            description
        }
        riskLibraryCategory {
            id
            name
            active
        }
        responsibleFunction {
            id
            name
            active
            organisationId
            studyId
        }
        reviewFrequencyPeriod
        reviewFrequencyUnits
        controlStrategy {
            id
            name
        }
    }
    ${LibraryRiskFragment}
`;

export const UserAdminFragment = gql`
    fragment UserAdminFields on User {
        ...UserAndRoleFields
        studies {
            id
            studyNumber
            code
            modules
            members {
                id
                createdById
            }
        }
        organisation {
            id
            name
        }
    }
    ${UserAndRoleFragment}
`;

export const ReportSignatoryFragment = gql`
    fragment ReportSignatoryFields on ReportSignatory {
        id
        recordId
        firstName
        auditId
        lastName
        title
        role
        created
        updated
        createdBy {
            id
            firstName
            lastName
        }
        updatedBy {
            id
            firstName
            lastName            
        }
        recordId
    }    
`;

export const NamedItemFragment = gql`
    fragment NamedItemFields on NamedItem {
        auditId
        id
        recordId
        organisationId
        associatedItemId
        studyId
        name
        type
        active
        created
        updated
        createdBy {
            id
            firstName
            lastName
        }
        updatedBy {
            id
            firstName
            lastName
        }
    }
`;

export const NamedItemFragmentLOV = gql`
    fragment NamedItemFieldsLOV on NamedItem {
        id
        organisationId
        associatedItemId
        studyId
        name
        type
        active
        comments
        created
        updated
        recordId
        createdBy {
            id
            firstName
            lastName
        }
        updatedBy {
            id
            firstName
            lastName
        }
    }
`;

export const IsmpFragment = gql`
    fragment IsmpFields on ISMP {
        auditId
        id
        studyId
        monitoringStrategy
        purpose
        scope
        updated
        updatedBy {
            id
            firstName
            lastName
        }
        created        
    }    
`;

export const AuditLogFragment = gql`
    fragment AuditLogFields on AuditRecord {
        id
        action
        created
        userEmail
        userFirstName
        userLastName
        reason
        area
        description
        studyId
        objectType
        data
        before
        itemId
        parentId
        itemRecordId
        system
        recordId
        after
        userId
        userIpAddress
        originService,
        serviceItemId
    }
`;

export const AuditRecordIdPrefixFragment = gql`
    fragment AuditRecordIdPrefixFields on AuditRecordIdPrefix {
        service
        prefix
    }
`;

export const ProtocolDocumentFragment = gql`
    fragment ProtocolDocumentType on ProtocolDocument {
        id
        auditId
        code
        created
        createdBy {
            id
            firstName
            lastName
        }
        createdById
        updated
        updatedBy {
            id
            firstName
            lastName
        }
        deleted
        documentName
        riqId
        status
        studyId
        version
    }
`;

export const ProtocolDocumentAuditFragment = gql`
    fragment ProtocolDocumentAuditType on ProtocolDocument {
        id
        auditId
        updated
        code
        documentName
        riqId
        status
        studyId
        version
        deleted
    }
`;

const AxisFields = gql`
    fragment RowDataFields on SeriesRowData {
        source
        rule {
            field1
            operator1
            value1
            logicalOperator
            field2
            operator2
            value2
        }
    }

    fragment AxisFields on Axis {
        type
        title
        unit
        dateSeries {
            id
            label
            rowData {
                ...RowDataFields
            }
            plotLabel
            type
            startField
            endField
        }
        numericSeries {
            id
            label
            rowData {
                ...RowDataFields
            }
            type
            yPlotField
            dateField
            threshold {
                value {
                    upper
                    lower
                }
                field {
                    upper
                    lower
                }
            }
        }
    }
`;
export const PanelListFragment = gql`
    fragment PanelListFields on Panel {
        auditId
        id
        configId
        organisationId
        studyId
        name
        active
        weight
        includeInSiteScoring
        created
        recordId
        createdBy {
            email
            firstName
            lastName
        }
        updated
        updatedBy {
            email
            firstName
            lastName
        }
        decision
        panelType
        modelParams{
            type
            rateGroupType
        }
        graph {
            type
            plotTitle
            xTitle
            yTitle            
        }
        panelTheme
        
        timeline {
            leftAxis {
                ...AxisFields
            }
            rightAxis {
                ...AxisFields
            }
        }
        
        triOpDateParameters {
            xAxisTitle
            yAxisTitle
            rowData
            amberMaxScore
            greenMaxScore
            amberThreshold
            redThreshold
            bigRedThreshold
            dataInclusionPeriod
            dataInclusionUnit
            minDataNeeded
            sortField
            yAxisDataField
            thresholdCalculation
            stableValue
            stableVariableType
            siteAverage
            dataPopulation
            yAxisColumnValue
        }
        
        proportionsOverTimeParameters {
            xAxisTitle
            yAxisTitle
            rowData
            rowDataDate
            rowDataFlag
            numeratorName
            denominatorName
            scaleBy
            eligibilityUnit
            eligibilityPeriod
            plotBy
            thresholdType
            upperAmber
            upperRed
            lowerAmber
            lowerRed
            benchmarkType
            studySpecificBenchmark
            qtl
            reportOutOfTolerance
            minData
            countryBenchmark
            xAxisPtsToShade
            cumulativeOrTimePeriod
            expectedVolume
            numberOfTimePeriods
            automationRange
        }
        
        eventRatesOverTimeParameters {
            minTimeOnStudyUnit
            minTimeOnStudyPeriod
            benchmarkType
            countryBenchmark
            numeratorName
            denominatorName
            eventName
            expectedVolume
            timepointsList
            lowerAmber
            lowerRed
            minSubjects
            xAxisPtsToShade
            plotBy
            cumulativeOrTimePeriod
            qtl
            reportOutOfTolerance
            rowData
            rowDataDate
            scaleBy
            studySpecificBenchmark
            thresholdType
            timepointDate
            upperAmber
            upperRed
            calcEndDates
            calcStartDate
            xAxisTitle
            yAxisTitle
            numberOfTimePeriods
            automationRange
        }
                    
        plotTitle
        xAxisLabel
        yAxisLabel
        associatedRisksIds
    }
    ${AxisFields}
`;

export const PanelFragment = gql`
    fragment PanelFields on Panel {
        ...PanelListFields
        modelParams {
            type
            rateGroupType
            amberCutoff
            benchmarkType
            benchmarkValue
            confidenceIntervalType
            dataInclusionPeriod
            dataInclusionUnit
            hideLower
            hideUpper
            minimumDataMovementScore
            plotBy
            rateBy
            lowerAmber
            upperAmber
            lowerBigRed
            upperBigRed
            lowerRed
            upperRed
            redCutoff
            siteScoring
            stableValue
            stableVariableType
            studyComparison
            unitMultiplier
            triThreshold1Type
            triThreshold2Type
            thresholdNum1
            thresholdNum2
            amberMaxScore
            greenMaxScore
        }
        timeline {
            leftAxis {
                ...AxisFields
            }
            rightAxis {
                ...AxisFields
            }
        }
        graph {
            type
            plotTitle
            plotThreshold
            xTitle
            yTitle
            displayBy
            displayUnits
            hideStudyColumns
            orderBy
            columnType
            categories {
                index
                min
                max
                clause
            }
            categoryRangeUnit
            categoryRangePeriod
            timePeriod
            seriesType
            numberOfSeries
            columnRangeCategory
            lineCalculation
            lineThresholdType
            thresholdNum1
            thresholdNum2
            series {
                label
                dataSource
                dataPointField
                dateField
                threshold
            }
            dataInclusionPeriod
            dataInclusionUnit
        }
        dataSource {
            dataSource
            yAxisDataField
            sortField
            countField
            rateByField
            rateByType
            timePeriod
            xAxisDataField
            xAxisEndDateFields
            useEarliestEndDate
            dateField
            flagField
            countFirstEventTypeOnly
        }
        studyOverviewExtraColumns {
            columnId
            label
            type
            dataSource
            dataField
            numeratorField
            denominatorField
        }
        scheduleId
        yAxisColumnValue
        associatedRisksIds
        risks {
            id
            statement
        }
    }   
    ${PanelListFragment}
    ${AxisFields}
`;

export const PanelForObservationFragment = gql`
    fragment PanelForObservationFields on Panel {
        id
        active
        graph {
            plotTitle
        }
        name
    }
`;

export const CriticalVariableAuditFragment = gql`
    fragment CriticalVariableAuditFields on CriticalVariable {
        id
        studyId
        auditId
        updated
        recordId
        description
        justification
        type
        responsibleVendor
        sourceSystem
        edcEntry
        dataSource
        isCritical
        isIsmp
        wasIsmp
        associatedRiskIds
        associatedRisks {
            id
            statement
        }
        associatedFunctionalDataReviews {
            id
            description
        }
        active
        controlIds
        controls {
            id
            description
        }
        vendorIds
        vendors {
            id
            name
            active
        }
        eDCEntryId,
        cTQEDCEntry {
            name
        },
        typeId,
        cTQType {
            name
        }
        riqPendingRiskAssociations
        riqAcceptedRiskAssociations
        riqRejectedRiskAssociations
        riqAcceptedAndPromotedRiskAssociations
        associatedRiqRisks {
            id
            recordId
            statement
        }
    }
`;

export const RiskAuditFragment = gql`
    fragment RiskAuditFields on Risk {
        id
        auditId
        updated
        studyId
        libraryReferenceId
        ownerId
        reviewStatus
        owner {
            id
            firstName
            lastName            
            active
        }
        recordId
        internalOnly
        controlStrategy {
            id
            name
        }
        categoryId
        category {
            id
            name
        }
        statement
        associatedVariableIds
        associatedVariables {
            id
            active
            description            
        }
        dateLastReviewed
        dateIdentified
        closedDate
        status
        reviewFrequencyPeriod
        reviewFrequencyUnits
        nextReviewDate
        controlIds
        controls {
            id            
            description          
        }              
        responsibleFunctionId
        responsibleFunction {
            name
        }
        evaluationHistory {
            impact
            detectability
            likelihood
            id
            changeReason
            rationale
        }
        observationIds
        observations {
            id
            description
        }
        qualityIssue {
            name
        }
        associatedControlsHubPanelIds
        controlsHubPanels {
            id
            plotTitle
        }
        riqPendingVariableAssociations
        riqAcceptedVariableAssociations
        riqRejectedVariableAssociations
        riqAcceptedAndPromotedVariableAssociations
        associatedRiqVariables {
            id
            recordId
            description
        }
    }
`;

export const UserAuditFragment = gql`
    fragment UserAuditFields on User {
        id
        auditId
        updated
        firstName
        lastName
        email
        failedLogins
        systemAdminLevel
        phone
        isEnabled
        recordId        
    }
`;

export const CentralMonitoringActionFragment = gql`
    fragment CentralMonitoringActionFields on CentralMonitoringObservationAction {
        auditId
        audit {
            reason
            created
            userFirstName
            userLastName
        }
        id
        created
        createdBy {
            id
            firstName
            lastName
        }
        updated
        updatedBy {
            id
            firstName
            lastName
        }
        deleted
        ownerId
        recordId
        description
        result
        actionTaken
        rootCause
        completedDate
        requestDate
        expectedCompleteDate
        owner {
            firstName
            lastName
            active
            email
        }
    }
`;

export const CentralMonitoringObservationAndActionFragment = gql`
    fragment CentralMonitoringObservationAndActionFields on CentralMonitoringObservation {
        id
        siteId
        studyId
        panelId
        auditId
        audit {
            reason
            created
            userFirstName
            userLastName
        }
        created
        createdBy {
            id
            firstName
            lastName
        }
        updated
        updatedBy {
            id
            firstName
            lastName
        }
        observationDate
        deleted
        recordId
        description
        potentialCause
        confirmedCause
        dataSetId
        panel {
            id
            name
        }
        panelId
        owner {
            id
            firstName
            lastName
            active
        }
        ownerId
        comments
        closedDate
        closedDatePopulated
        qtlExceeded
        type
        observationDate
        actions {
            ...CentralMonitoringActionFields
        }
        dataSet {
            id
            created
            dataCutDate
        }
    }    
    ${CentralMonitoringActionFragment}
`;

export const DashboardFragment = gql`
    fragment DashboardFields on Dashboard {
        id
        configId
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        studyId
        type
        panels {
            index
            panelId
            childPanels {
                index
                panelId
            }
            dataReviews {
                index
                dataReviewId
            }
        }
    }  
`;

export const DashboardAuditFragment = gql`
    fragment DashboardAuditFields on Dashboard {
        id
        auditId
        studyId
        type
        updated
        panels {
            index
            panelId
            childPanels {
                index
                panelId
            }
            dataReviews {
                index
                dataReviewId
            }
        }
    }  
`;

export const DataSetFragment = gql`
    fragment DataSetFields on DataSet {
        auditId
        id
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        created
        createdBy {
            firstName
            lastName
            email
        }
        studyId
        dataCutDate
        processPanelsDate
        recordId
        notes
        active
        reports {
            reportDate
            reportId
            includeAllSites
        }
    }
`;

export const ReportFragment = gql`
    fragment ReportFields on Report {
        id
        recordId
        studyId
        filename
        templateName
        fileType
        size
        status
        comments
        contents
        created
        recordId
        auditId
        updated
        createdBy {
            id
            firstName
            lastName
        }
        updatedBy {
            id
            firstName
            lastName
        }
        dataSet {
            dataCutDate
            reports {
                reportId
                includeAllSites
            }
        }
    }
`;

export const SiteReviewFragment = gql`
    fragment SiteReviewFields on SiteReview {
        auditId
        id
        created
        createdBy {
            firstName
            lastName
            email
        }
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        recordId
        siteId
        reviewer
        summary
        reviewed
    }
`;

export const AuditLogChangeHistoryFragment = gql`
    fragment AuditLogChangeHistoryFields on AuditLogChangeHistory {
        auditId
        id
        created
        createdBy {
            firstName
            lastName
            email
        }
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        recordId
    }
`;

export const DataSetReviewFragment = gql`
    fragment DataSetReviewFields on DataSetReview {
        auditId
        id
        recordId
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        studyId
        dataSetId
        created
        createdBy {
            firstName
            lastName
            email
        }
        startDate
        completedDate
        summary
        reviewer
        reviewed
        reportBlobInfoId
        reportStatus
        includeOnlyReviewedSites
        siteReviews {
            ...SiteReviewFields
        }        
    }  
    ${SiteReviewFragment}
`;

export const ExtraDataFieldFragment = gql`
    fragment ExtraDataFields on ExtraDataField {
        id
        studyId
        recordId
        created
        createdBy {
            firstName
            lastName
            email
        }
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        friendlyName
        table
        sourceFieldName
        fieldType
        isCalculated
        active
        auditId
        configId
    }  
`;

export const DataReviewTableFragment = gql`
    fragment DataReviewTableFields on DataReviewTable {
        auditId
        id
        studyId
        recordId
        configId
        created
        createdBy {
            firstName
            lastName
            email
        }
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        type
        dataSource
        description
        active
        name
        columns {
            columnId
            heading
            type
            level
            dataSource
            dataField
            numeratorField
            denominatorField
            panelField
            panelId
        }
    }
`;

// AllColumns walks the table's full version history (more expensive than Columns), so it's kept
// out of DataReviewTableFragment and only requested by callers that genuinely need a column's
// last known heading after it's been removed - e.g. flagging a stale "on watch" Review Plan
// column, or resolving a column's name in the Review Plan audit log.
export const DataReviewTableAllColumnsFragment = gql`
    fragment DataReviewTableAllColumnsFields on DataReviewTableColumnWithStatus {
        columnId
        heading
        dataField
        active
    }
`;

export const DataLoadFragment = gql`
    fragment DataLoadFields on DataLoad {
        id
        dataSetId
        dataCutDate
        steps {
            date
            action
            state
            description
            completedBy
            logIds
            finished
            version
            importCounts {
                siteIssuesAdded
                siteIssuesModified
                siteProtocolDeviationsAdded
                siteProtocolDeviationsModified
                siteVisitsAdded
                siteVisitsModified
                sitesAdded
                sitesModified
                subjectAdverseEventsAdded
                subjectAdverseEventsModified
                subjectCrfsAdded
                subjectCrfsModified
                subjectProtocolDeviationsAdded
                subjectProtocolDeviationsModified
                subjectQueriesAdded
                subjectQueriesModified
                subjectVisitsAdded
                subjectVisitsModified
                subjectsAdded
                subjectsModified
                subjectInformedConsentsAdded
                subjectInformedConsentsModified
                subjectAssessmentsAdded
                subjectAssessmentsModified
            }
        }
    }  
`;

export const CentralMonitoringObservationAuditFragment = gql`
    fragment CentralMonitoringObservationAuditFields on CentralMonitoringObservation {
        id
        siteId
        studyId
        panelId
        auditId
        created
        createdBy {
            id
            firstName
            lastName
        }
        updated
        updatedBy {
            id
            firstName
            lastName
        }
        observationDate
        deleted
        recordId
        description
        potentialCause
        confirmedCause
        dataSetId
        panel {
            id
            name
        }
        panelId
        owner {
            id
            firstName
            lastName
            active
        }
        ownerId
        comments
        closedDate
        qtlExceeded
        type
        observationDate
        dataSet {
            id
            created
            dataCutDate
        }
        actions {
            id
            description
        }
    }
`;

export const ImportStateFragment = gql`
    fragment ImportStateFields on ImportState {
        action
        calculatedFieldsDescription
        calculatedFieldsHasData
        created
        createdBy {
            firstName
            lastName
            id
        }
        updated
        updatedBy {
            firstName
            lastName
            id
        }
        importFinished
        dataCutDate
        dataSetId
        importedItemCounts {
            siteIssuesAdded
            siteIssuesModified
            siteProtocolDeviationsAdded
            siteProtocolDeviationsModified
            siteVisitsAdded
            siteVisitsModified
            subjectAdverseEventsAdded
            subjectAdverseEventsModified
            subjectCrfsAdded
            subjectCrfsModified
            subjectProtocolDeviationsAdded
            subjectProtocolDeviationsModified
            subjectQueriesAdded
            subjectQueriesModified
            subjectVisitsAdded
            subjectVisitsModified
            subjectsAdded
            subjectsModified
        }
        importerVersion
        processPanelsDescription
        processPanelsHasData
        state        
    }
`;

export const RowDataSourceFragment = gql`
    fragment RowDataSourceFields on RowDataSourceInfo {
        id
        name
        tags
        table
        default
    }  
`;

export const EventRecordFragment = gql`
    fragment EventRecordFields on EventRecord {
        id
        timestamp
        ipAddress
        organisationKey
        userId
        affectedUserId
        affectedOrganisationMemberId
        userName
        userEmail
        type
        data
    }
`;

export const ReportDefinitionFragment = gql`
    fragment ReportDefinitionFields on ReportDefinition {
        id
        userId
        deleted
        name
        description
        includeInternalRisks
        includeNotApplicableRisks
        includeDraftRisks
        includeObsoleteRisks
        recordId
        tabs {
            tabType
            columns {
                index
                dataField
            }
        }
        created
        createdBy {
            id
            firstName
            lastName
        },
        updatedBy {
            id
            firstName
            lastName
        }
        updated
    }
`;

export const ReportDefinitionAuditFragment = gql`
    fragment ReportDefinitionAuditFields on ReportDefinition {
        id
        auditId
        updated
        recordId
        userId
        name
        description
        includeInternalRisks
        includeNotApplicableRisks
        tabs {
            tabType
            columns {
                index
                dataField
            }
        }
    }
`;

export const CustomReportResultFragment = gql`
    fragment CustomReportResultFields on CustomReport {
        name
        fileName
        tabs {
            columns
            data {
                row
            }
            tabType
        }
    }
`;

export const StudyOverviewTableFragment = gql`
    fragment StudyOverviewTableFields on StudySummaryOverviewTable {
        panel
        columnSummary {
            change
            current
            previous
            title
            columnOrder
            type
        }
        panelColumnXAxis {
            change
            current
            previous
            title
            columnOrder
            type
        }
        panelColumnYAxis {
            change
            current
            previous
            title
            columnOrder
            type
        }
        panelExtraColumns {
            change
            current
            previous
            title
            columnOrder
            type
        }
    }
`;

export const DataViewConfigurationFragment = gql`
    fragment DataViewConfigurationFields on DataViewConfiguration {
        auditId
        id
        recordId
        configId
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        studyId
        created
        createdBy {
            firstName
            lastName
            email
        }
        name
        mainPanelId
        dataSource
        isActive
        displayIndex
        panelIds
        dataTableIds
        dataFilterIds
        inUse
        unfilterablePanelIds
    }
`;

export const DataViewConfigurationAuditFragment = gql`
    fragment DataViewConfigurationAuditFields on DataViewConfiguration {
        auditId
        id
        recordId
        configId
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        studyId
        created
        createdBy {
            firstName
            lastName
            email
        }
        name
        mainPanelId
        dataSource
        isActive
        displayIndex
        panelIds
        dataTableIds
        dataFilterIds
        unfilterablePanelIds
    }
`;

export const DataViewFilterFragment = gql`
    fragment DataViewFilterFields on DataViewFilter {
        auditId
        id
        recordId
        configId
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        studyId
        created
        createdBy {
            firstName
            lastName
            email
        }
        fieldKey
        fieldName
        label
        dataSource
        displayIndex
        isDefault
    }
`;

export const DataViewFilterAuditFragment = gql`
    fragment DataViewFilterAuditFields on DataViewFilter {
        auditId
        id
        recordId
        configId
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        studyId
        created
        createdBy {
            firstName
            lastName
            email
        }
        fieldKey
        label
        dataSource
        displayIndex
        isDefault
    }
`;

export const DataViewFilterListFragment = gql`
    fragment DataViewFilterListFields on DataViewFilter {
        id
        label
        fieldKey
        recordId
    }
`;


export const StudyDashboardConfigFragment = gql`
    fragment StudyDashboardConfigurationFields on StudyDashboardConfiguration {
        id
        dataSetFilter
        dataSetsCount
        recordId
    }
`;

export const WidgetFragment = gql`
    fragment WidgetFields on Widget {
        id
        iconName
        tableCode
        typeCode
        displayName
        fieldKey
        fieldName
        useOnSiteGrid
        recordId
        studyId
    }`;

export const ControlsHubWidgetFragment = gql`
    fragment ControlsHubWidgetFields on ControlsHubWidget {
        id
        iconName
        tableCode
        typeCode
        displayName
        fieldKey
        fieldName
        recordId
        studyId
    }`;

export const getAllControlsHubWidgetsForStudyFragment = gql`
    fragment getAllControlsHubWidgetsForStudyFields on ControlsHubWidget {
        ...ControlsHubWidgetFields
    }
    ${ControlsHubWidgetFragment}
`;

export const StudyDashboardConfigWidgetFragment = gql`
    fragment StudyDashboardConfigurationWidgetFields on StudyDashboardConfigWidget {
        id
        widgets {
            ...WidgetFields
        }
    }
    ${WidgetFragment}
`;

export const StudyDashboardConfigPanelFragment = gql`
    fragment StudyDashboardConfigurationPanelFields on StudyDashboardConfigPanels {
        id
        panelRefs
    }
`;

export const DataViewConfigResultsFragment = gql`
    fragment DataViewConfigResults on DataViewConfigurationResult {
        result { ...DataViewConfigurationFields }
        validation
    }
    ${DataViewConfigurationFragment}
`;

export const DataViewConfigNamesFragment = gql`
    fragment DataViewConfigNames on DataViewConfiguration {
        id
        name
        isActive
        inUse,
        panelIds,
        displayIndex,
        mainPanelId
    }
`;
export const DataViewFilterResultsFragment = gql`
    fragment DataViewFilterResults on DataViewFilterResult {
        result { ...DataViewFilterFields }
        validation
    }
    ${DataViewFilterFragment}
`;

export const StudyDashboardConfigurationAuditFragment = gql`
    fragment StudyDashboardConfigurationAuditFields on StudyDashboardConfiguration {
        id
        auditId
        recordId
        configId
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        studyId
        created
        createdBy {
            firstName
            lastName
            email
        }
        dataSetFilter
        dataSetsCount
        recordId
        
    }`;

export const StudyDashboardConfigWidgetAuditFragment = gql`
    fragment StudyDashboardConfigWidgetAuditFields on StudyDashboardConfigWidget {
        id
        auditId
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        studyId
        created
        createdBy {
            firstName
            lastName
            email
        }
        widgets {
            ...WidgetFields
        }
    }
    ${WidgetFragment}
`;

export const WidgetAuditFragment = gql`
    fragment WidgetAuditFields on Widget {
        id
        auditId
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        studyId
        created
        createdBy {
            firstName
            lastName
            email
        }
    }`;

export const StudyDashboardConfigPanelAuditFragment = gql`
    fragment StudyDashboardConfigPanelAuditFields on StudyDashboardConfigPanels {
        id
        auditId
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        studyId
        created
        createdBy {
            firstName
            lastName
            email
        }
        panelRefs
    }`;

export const getAllWidgetsForStudyFragment = gql`
    fragment getAllWidgetsForStudyFields on Widget {
        ...WidgetFields
    }
    ${WidgetFragment}
`;

export const getStudyHubStudyFieldsConfigFragment = gql`
    fragment studyHubStudyFieldsConfigFields on StudyHubStudyFieldsConfig {
        numGreenSites
        numAmberSites
        numRedSites
        studySiteMean
        countrySiteMean
        studyRate
        siteRate
    }
`;

export const getStudyHubPanelLineSelectorConfigFragment = gql`
    fragment studyHubPanelLineSelectorConfigFields on StudyHubPanelLineSelectorConfig {
        id
        studySiteMean
        studyRate
    }
`;

export const getStudyHubSiteFieldsConfigFragment = gql`
fragment studyHubSiteFieldsConfigFields on StudyHubSiteFieldsConfig {
    score
    numGreenKRIs
    numAmberKRIs
    numRedKRIs
    numGreenDecisionMakingKRIs
    numAmberDecisionMakingKRIs
    numRedDecisionMakingKRIs
    activeDate
    closedDate
    investigator
    monitor
}`;

export const StudyHubMonitoringToolkitConfigFragment = gql`
    fragment StudyHubMonitoringToolkitConfigFields on StudyHubMonitoringToolkitConfig {
        id
        allowKRIReviews
        recordId
    }
`;

export const CriticalVariableFragment = gql`
    fragment CriticalVariableFields on CriticalVariable {
        id
        created
        deleted
        studyId
        recordId
        description
        justification        
        sourceSystem
        dataSource
        isCritical
        active
        riskCount
        updated
        associatedRiskIds
        createdBy {
            id
            firstName
            lastName
        }
        updatedBy {
            id
            firstName
            lastName
        }
        cTQEDCEntry {
            id
            name
            active
        }
        vendors {
            id
            name
            active
        }
        cTQType {
            id
            name
            active
        }
        associatedFdrLibraryIds
        associatedFunctionalDataReviewIds
        fDRCount
        commentCounts {
            open
            all
        }
    }
`;

export const CriticalVariableAssociatedRiskFragment = gql`
    fragment CriticalVariableAssociatedRiskFields on Risk {
        id
        statement
        activeControlCount
        dateIdentified
        closedDate
        category {
            id
            name
            active
        }
        status
        reviewStatus
        owner {
            id
            active
            fullName
            firstName
            lastName
        }
        internalOnly
        created
        deleted
        studyId
        recordId
        libraryReferenceId
        score {
            score
            colour
            movement
            sortMovement
            prevScore
            prevColour
        }
        active
        updated
        createdBy {
            id
            firstName
            lastName
        }
        updatedBy {
            id
            firstName
            lastName
        }
        ownerId
        vendors {
            id
            name
            active
        }
        countries {
            code,
            name
        }
        responsibleFunction {
            id
            name
            active
        }
        controlStrategy {
            id
            name
            active
        }
        reviewRequired
        reviewFrequencyPeriod
        reviewFrequencyUnits
        associatedVariableIds
        controlIds
        dateLastReviewed
        qualityIssue {
            id
            name
            active
        }
        qualityIssueReference
    }
`;

export const CriticalToQualityFactorWidgetFragment = gql`
    fragment CriticalToQualityFactorWidgetFields on CriticalToQualityFactorWidget {
        type
        value
        show
    }
`;

export const getSubjectMonitoringConfigFragment = gql`
    fragment SubjectMonitoringConfigFields on DataListingsConfig {
        studyId,
        id,
        configId,
        dataListingsItems {
            datatableId,
            name,
            recordId
        }
    }`;

export const SubjectMonitoringConfigFragment = gql`
    fragment subjectMonitoringConfigFields on DataListingsConfig {
        studyId,
        id,
        configId,
        dataListingsItems {
            datatableId,
            name,
            recordId
        }
    }
`;

export const getDataListingsFragment = gql`
    fragment DataListingsFields on DataReviewTable {
        id
        recordId
        configId
        type
        dataSource
        description
        active
        name
    }
`;

export const subjectProfileDetailsConfigurationFragment = gql`
    fragment SubjectProfileDetailsConfigurationFields on SubjectProfileDetailsConfiguration {
        id,
        studyId,

        subjectProfileDetailsConfigurationItem {
            personalInformation {
                label
                ageAtEnrolment
                gender
                race
                ethnicity
                latestConsentDate
                latestConsentVersion
            }
            subjectStatus {
                label
                status
                screening
                screenFail
                screenFailReason
                enrolled
                randomized
                fUStart
                fUCompleted
                studyDisc
                studyDiscReason
                lastVisitNameAndDate
                numberOfAEs
                numberOfSAEs
                numberOfAESIs
                numberOfPDs
                numberOfQueriesOpenAll
                combiVisits
                combiAssessments
            }
            treatmentStatus {
                label
                cohort
                treatmentArm
                txStart
                txDisc
                txDiscReason
                txCompleted
            }
            siteInformation {
                label
                status
                country
                activeDate
                closedDate
                monitor
                investigatorName
                subjectsScreened
                subjectsScreenFailed
                subjectEnrolled
                subjectRandomized
                subjectTxStart
                subjectTxDisc
                subjectsTxComplete
                subjectsFUStart
                subjectsFUComplete
                numberOfAEs
                numberOfSAEs
                numberOfAESIs
                numberOfPDs
                numberOfQueriesOpenAll
            }
            timeline {
                startDate
                endDateFields
                sortAscending
                milestoneDateFields
                showTagsAsMilestones
                panels {
                    id
                    plotTitle 
                }
            }
        }
    }
`;

export const RiskFragment = gql`
    fragment RiskFields2 on Risk {
        id
        created
        deleted
        studyId
        createdById
        libraryReferenceId
        evaluationHistory {
            deleted
        }
        ownerId
        internalOnly
        recordId
        reviewStatus
        controlStrategy {
            id
            name
            active
        }
        category {
            id
            name
            active
        }
        statement
        associatedVariableIds
        dateIdentified
        dateLastReviewed
        closedDate
        status
        reviewRequired
        reviewFrequencyPeriod
        reviewFrequencyUnits
        nextReviewDate
        controlIds
        updated
        active
        vendorIds
        createdBy {
            id
            firstName
            lastName
        }
        updatedBy {
            id
            firstName
            lastName
        }
        owner {
            id
            fullName
            active
        }
        vendors {
            id
            name
            active
        }
        countries {
            code,
            name
        }
        responsibleFunction {
            id
            name
            active
        }
        qualityIssue {
            id
            name
            active
        }
        qualityIssueReference
    }
`;

export const LibraryFunctionalDataReviewsFragment = gql`
    fragment LibraryFunctionalDataReviewFields on LibraryFunctionalDataReview {
        id
        recordId
        description
        active
        responsibleFunction {
            id
            name
            active
        }
        created
        updated
        createdBy {
            id
            firstName
            lastName
        }
        updatedBy {
            id
            firstName
            lastName
        }
    }
`;

export const LibraryFunctionalDataReviewsAuditFragment = gql`
    fragment LibraryFunctionalDataReviewAuditFields on LibraryFunctionalDataReview {
        id
        recordId
        description
        active
        responsibleFunction {
            id
            name
            active
        }
        created
        updated
        createdBy {
            id
            firstName
            lastName
        }
        updatedBy {
            id
            firstName
            lastName
        }
        auditId
    }
`;



export const AssignLibraryFunctionalDataReviewFragment = gql`
    fragment AssignLibraryFunctionalDataReviewFields on LibraryFunctionalDataReview {
        id
        code
        deleted
        active
        responsibleFunctionId
        description
        recordId
        responsibleFunction {
            id
            name
            active
        }
    }
`;

export const FunctionalDataReviewAuditFragment = gql`
    fragment FunctionalDataReviewAuditFields on FunctionalDataReview {
        id
        auditId
        updated
        recordId
        responsibleFunctionId
        criticalToQualityFactor {
            id
            description
        }
        description
        active
        responsibleFunction {
            id
            name
            active
        }
    }
`;

export const FunctionalDataReviewsIncludingCtQfDetailFragment = gql`
    fragment FunctionalDataReviewsIncludingCtQfDetailFields on FunctionalDataReviewWithCtQfDetail {
        id
        responsibleFunction {
            id
            name
            active
        }
        description
        createdBy {
            fullName
        }
        createdDate
        updatedBy {
            fullName
        }
        updatedDate
        ctQfId
        noRisks
        ctQfDescription
        active
        type {
            id
            name
            active
        }
        justification
        system
        dataSource
        edcEntry {
            id
            name
            active
        }
        vendors {
            id
            name
            active
        }
        critical
        ctQfCreatedBy {
            fullName
        }
        ctQfCreatedDate
        ctQfUpdatedBy {
            fullName
        }
        ctQfUpdatedDate   
        recordId
        ctQfRecordId
        comment
    }
`;

export const FunctionalDataReviewsByStudyAndCtqFragment = gql`
    fragment FunctionalDataReviewsByStudyAndCtqFields on FunctionalDataReview {
        id
        recordId
        responsibleFunction {
            id
            name
            active
        }
        description
        comment
        created
        updated
        createdBy {
            fullName
        }
        updatedBy {
            fullName
        }
    }
`;

export const ControlsHubConfigWidgetFragment = gql`
    fragment ControlsHubConfigWidgetFields on ControlsHubConfigWidget {
        id
        widgets {
            ...ControlsHubWidgetFields
        }
    }
    ${ControlsHubWidgetFragment}
`;

export const ControlsHubConfigWidgetAuditFragment = gql`
    fragment ControlsHubConfigWidgetAuditFields on ControlsHubConfigWidget {
        id
        auditId
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        studyId
        created
        createdBy {
            firstName
            lastName
            email
        }
        widgets {
            ...ControlsHubWidgetFields
        }
    }
    ${ControlsHubWidgetFragment}
`;


export const ControlsHubWidgetAuditFragment = gql`
    fragment ControlsHubWidgetAuditFields on ControlsHubWidget {
        id
        auditId
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        studyId
        created
        createdBy {
            firstName
            lastName
            email
        }
    }`;

export const ControlsHubConfigPanelFragment = gql`
    fragment ControlsHubConfigurationPanelFields on ControlsHubConfigPanels {
        id
        panels {
            panelRef
            dataViewRef
        }
    }
`;

export const ControlsHubConfigPanelResolvedFragment = gql`
    fragment ControlsHubConfigurationPanelResolvedFields on ControlsHubConfigPanelsResolved {
        panels {
            panelRef
            plotTitle
            active
        }
    }
`;

export const ControlsHubConfigPanelAuditFragment = gql`
    fragment ControlsHubConfigPanelAuditFields on ControlsHubConfigPanels {
        id
        auditId
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        studyId
        created
        createdBy {
            firstName
            lastName
            email
        }
        panels {
            panelRef
            dataViewRef
        }
    }`;

export const CriticalVariablesForRiskFragment = gql`
    fragment CriticalVariablesForRiskFields on CriticalVariable {
        id
        recordId
        description
        justification
        sourceSystem
        dataSource
        active
        isCritical
        riskCount
        cTQType {
            id
            name
            active
        }
        cTQEDCEntry {
            id
            name
            active
        }
        vendors {
            id
            name
            active
        }
        created
        createdBy {
            firstName
            lastName
            email
        }
        updated
        updatedBy {
            firstName
            lastName
            email
        }
        studyId
    }`;

export const CriticalVariableListItemFragment = gql`
    fragment CriticalVariableListItemFields on CriticalVariable {
        id
        description
        active
    }
`;

export const  RiskDashboardConfigFragment = gql`
fragment RiskDashboardConfigFields on RiskDashboardConfig {
    id
    panels {
        panelId
        displayOrder
    }
}`;

export const RiskAssessmentTagListItemFragment = gql`
    fragment RiskAssessmentTagListItemFields on RiskAssessmentTag {
        id        
        recordId
        name
        active
        created
        createdBy {
            firstName
            lastName
        }        
        updated
        updatedBy {
            firstName
            lastName
        }
    }
`;

export const RiskAssessmentCmControlChartsFragment = gql`
    fragment RiskAssessmentCmControlChartItemFields on Panel {
        id
        recordId
        name
        xAxisLabel
        yAxisLabel
    }
`;

export const RiskCommentPocFragment = gql`
    fragment RiskCommentPocFields on RiskPocComment {
        parentId
        publisherUserId
        id
    }
`;

export const GridStateFragment = gql`
fragment GridStateFields on GridState {
    id
    gridId
    userId
    name
    isFavourite
    gridStateData
    isDefault
}`;

export const IntegrationConfigMetadataFragment = gql `
    fragment IntegrationConfigMetadataFields on IntegrationConfigMetadata {
        id,
        createdBy {
            firstName
            lastName
        }
        uploadedDate,
        isActive,
        fileName
    }
`;

export const IntegrationFieldsAndCreatedFragment = gql `
    fragment IntegrationFieldsAndCreatedFields on IntegrationFieldsAndCreatedType {
        createdDate,
        fields
    }
`;


export const ControlsHubChartConfigFragment = gql`
    fragment ControlsHubChartConfigFields on ControlsHubChartConfig {
        id
        studyId
        cutoffDate
}`;
export const RisksAndControlsLovUsageFragment = gql`
    fragment RisksAndControlsLovUsageFields on Risk {
        id
        studyId
        categoryId
        controlStrategyId
        observations {
            id
            classificationId
        }
    }
`;

export const StudyControlsLovUsageFragment = gql`
    fragment StudyControlsLovUsageFields on StudyControl {
        id
        studyId
        typeId
        controlPhaseId
    }
`;

export const LibraryRisksLovUsageFragment = gql`
    fragment LibraryRisksLovUsageFields on LibraryRisk {
        id
        riskCategoryId
        controlStrategyId
        responsibleFunctionId
    }
`;

// Control Type LOV maps to LibraryControl.controlTypeId — confirmed live via add-library-control/index.tsx:230. typeId is unused/legacy.
export const LibraryControlsLovUsageFragment = gql`
    fragment LibraryControlsLovUsageFields on LibraryControl {
        id
        controlTypeId
        controlPhaseId
        responsibleFunctionId
    }
`;

export const LibraryFunctionalDataReviewsLovUsageFragment = gql`
    fragment LibraryFunctionalDataReviewsLovUsageFields on LibraryFunctionalDataReview {
        id
        responsibleFunctionId
    }
`;
